import React, { useEffect, useState, ReactNode } from 'react';

import Tab, { TabGroup } from '@amzn/meridian/tab';
import Loader from '@amzn/meridian/loader';
import { FormattedMessage } from 'react-intl';

export interface ITabConfig<TabIDs extends string> {
  tabGroupAriaLabel: string;
  content: {
    [key in TabIDs]?: {
      translationId: string;
      defaultMessage: string;
      markup: ReactNode;
    };
  };
}

export type TabContent<TabIDs extends string> = {
  tabId: TabIDs;
  translationId: string;
  defaultMessage: string;
  markup: ReactNode;
};

interface IRenderTabsFromConfigProps<TabIDs extends string> {
  defaultTab: TabIDs;
  tabConfig: ITabConfig<TabIDs> | undefined;
  styles?: {
    parentTabsContainer?: any;
  };
  onTabSwitch?: (...args: any) => void;
}

const RenderTabsFromConfig = <TabIDs extends string>({
  defaultTab,
  tabConfig,
  styles,
  onTabSwitch,
}: IRenderTabsFromConfigProps<TabIDs>) => {
  const [currentTab, setCurrentTab] = useState<TabIDs>(defaultTab);
  const [content, setContent] = useState<TabContent<TabIDs>[] | undefined>(
    void 0
  );

  useEffect(() => {
    if (tabConfig?.content) {
      setContent(
        (Object.keys(tabConfig?.content) as TabIDs[]).map((key: TabIDs) => ({
          tabId: key,
          ...tabConfig.content[key],
        }))
      );
    }
  }, [tabConfig?.content]);

  useEffect(() => {
    if (onTabSwitch) {
      onTabSwitch(currentTab);
    }
  }, [currentTab]);

  return content?.length ? (
    <div className={styles?.parentTabsContainer}>
      <TabGroup
        value={currentTab}
        onChange={setCurrentTab}
        aria-labelledby={tabConfig?.tabGroupAriaLabel}
      >
        {content?.map(c => (
          <div style={{ marginRight: 8, marginLeft: 8 }}>
            <Tab aria-controls={`${c.tabId}-tabpanel`} value={c.tabId}>
              <FormattedMessage
                defaultMessage={c.defaultMessage}
                id={c.translationId ?? ''}
              />
            </Tab>
          </div>
        ))}
      </TabGroup>
      {content?.map(
        c =>
          currentTab === c.tabId && (
            <div
              role='tabpanel'
              id={`${c.tabId}-tabpanel`}
              aria-labelledby={c.tabId}
            >
              {c.markup}
            </div>
          )
      )}
    </div>
  ) : (
    <Loader />
  );
};

export default RenderTabsFromConfig;
